import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Select, Button, Spin } from 'antd';
import { DownloadOutlined, RiseOutlined } from '@ant-design/icons';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { CARD_STYLE } from '../../constants/theme';

const { Option } = Select;

export default function AnalyticsReports() {
  const [stats, setStats] = useState({
    totalGMV: 0, totalOrders: 0, activeDealers: 0,
    leadRevenue: 0, commission: 0, activeContractors: 0,
    supportTickets: 0, deliveryAgents: 0,
  });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      const [ordersSnap, usersSnap, ticketsSnap, agentsSnap, jobsSnap] = await Promise.all([
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'supportTickets')),
        getDocs(collection(db, 'deliveryAgents')),
        getDocs(collection(db, 'jobs')),
      ]);

      const orders = ordersSnap.docs.map(d => d.data());
      const users = usersSnap.docs.map(d => d.data());
      const tickets = ticketsSnap.docs.map(d => d.data());
      const agents = agentsSnap.docs.map(d => d.data());
      const jobs = jobsSnap.docs.map(d => d.data());

      const totalGMV = orders.reduce((s, o) => s + (o.orderValue || 0), 0);
      const commission = orders.reduce((s, o) => s + (o.commissionAmount || 0), 0);
      const leadRevenue = jobs.reduce((s, j) => s + ((j.leadsBought || 0) * (j.leadPrice || 0)), 0);

      setStats({
        totalGMV,
        totalOrders: orders.length,
        activeDealers: users.filter(u => u.type === 'dealer' && u.status === 'active').length,
        leadRevenue,
        commission,
        activeContractors: users.filter(u => u.type === 'contractor' && u.status === 'active').length,
        supportTickets: tickets.length,
        deliveryAgents: agents.length,
      });
    } catch (error) {
      console.error('Analytics load error:', error);
    }
    setLoading(false);
  };

  const GMV_DATA = [
    { month: 'Jan', gmv: 0 }, { month: 'Feb', gmv: 0 },
    { month: 'Mar', gmv: 0 }, { month: 'Apr', gmv: 0 },
    { month: 'May', gmv: stats.totalGMV },
  ];

  const REVENUE_MIX = [
    { name: 'Product Commission', value: stats.commission, color: '#C9A84C' },
    { name: 'Lead Sales', value: stats.leadRevenue, color: '#1890FF' },
  ];

  const KPI_CARDS = [
    { title: 'Total GMV', value: 'Rs.' + stats.totalGMV.toLocaleString(), sub: 'From orders', color: '#C9A84C', trend: stats.totalGMV > 0 ? 'Growing' : 'No orders yet' },
    { title: 'Total Orders', value: stats.totalOrders, sub: 'All time', color: '#1890FF', trend: stats.totalOrders > 0 ? 'Active' : 'No orders yet' },
    { title: 'Active Dealers', value: stats.activeDealers, sub: 'KYC approved', color: '#52C41A', trend: 'Onboarding' },
    { title: 'Lead Revenue', value: 'Rs.' + stats.leadRevenue.toLocaleString(), sub: 'From leads sold', color: '#FAAD14', trend: stats.leadRevenue > 0 ? 'Growing' : 'No leads sold' },
    { title: 'Commission Earned', value: 'Rs.' + stats.commission.toLocaleString(), sub: 'KamSathi revenue', color: '#722ED1', trend: 'On target' },
    { title: 'Active Contractors', value: stats.activeContractors, sub: 'Verified', color: '#993556', trend: 'Expanding' },
    { title: 'Support Tickets', value: stats.supportTickets, sub: 'All time', color: '#FF7A45', trend: 'Monitored' },
    { title: 'Delivery Agents', value: stats.deliveryAgents, sub: 'Registered', color: '#1D9E75', trend: 'Active' },
  ];

  return (
    <Spin spinning={loading}>
      <div>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 13, color: '#7A8A96' }}>
            Live data from Firebase — updates as real orders flow in
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Select value={period} onChange={setPeriod} style={{ width: 140 }}>
              <Option value="week">This Week</Option>
              <Option value="month">This Month</Option>
              <Option value="quarter">This Quarter</Option>
            </Select>
            <Button icon={<DownloadOutlined />}
              style={{ color: '#C9A84C', borderColor: 'rgba(201,168,76,0.3)' }}>
              Export Report
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {KPI_CARDS.map((kpi, i) => (
            <Col xs={12} lg={6} key={i}>
              <Card style={CARD_STYLE} bodyStyle={{ padding: 16 }}>
                <div style={{ fontSize: 11, color: '#7A8A96', marginBottom: 4 }}>{kpi.title}</div>
                <div style={{ fontSize: 22, fontWeight: 600, color: kpi.color }}>{kpi.value}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                  <span style={{ fontSize: 11, color: '#4A4A47' }}>{kpi.sub}</span>
                  <span style={{ fontSize: 11, color: '#52C41A' }}>
                    <RiseOutlined /> {kpi.trend}
                  </span>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Charts */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={16}>
            <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
              <div style={{ color: '#C9A84C', fontWeight: 500, marginBottom: 16 }}>Monthly GMV Growth</div>
              {stats.totalGMV === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#4A4A47' }}>
                  No orders yet — chart will populate as orders come in
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={GMV_DATA}>
                    <defs>
                      <linearGradient id="gmvGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#C9A84C" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#C9A84C" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="month" stroke="#7A8A96" />
                    <YAxis stroke="#7A8A96" />
                    <Tooltip contentStyle={{ background: '#1A1A1A', border: '0.5px solid rgba(201,168,76,0.3)', borderRadius: 8 }} />
                    <Area type="monotone" dataKey="gmv" stroke="#C9A84C" fill="url(#gmvGrad)" name="GMV (Rs.)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
              <div style={{ color: '#C9A84C', fontWeight: 500, marginBottom: 16 }}>Revenue Mix</div>
              {stats.commission === 0 && stats.leadRevenue === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#4A4A47' }}>
                  No revenue yet — will populate as orders flow in
                </div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={REVENUE_MIX} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value">
                        {REVENUE_MIX.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => 'Rs.' + v.toLocaleString()} />
                    </PieChart>
                  </ResponsiveContainer>
                  {REVENUE_MIX.map((item, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.color }} />
                        <span style={{ color: '#B0B8C1' }}>{item.name}</span>
                      </div>
                      <span style={{ color: item.color, fontWeight: 500 }}>Rs.{item.value.toLocaleString()}</span>
                    </div>
                  ))}
                </>
              )}
            </Card>
          </Col>
        </Row>

        {/* Coming Soon Banner */}
        <div style={{
          marginTop: 24, padding: '16px 20px',
          background: 'rgba(201,168,76,0.06)',
          border: '0.5px solid rgba(201,168,76,0.2)',
          borderRadius: 10, textAlign: 'center',
        }}>
          <div style={{ color: '#C9A84C', fontWeight: 500, marginBottom: 6 }}>
            Full Analytics Coming in Phase 2
          </div>
          <div style={{ color: '#7A8A96', fontSize: 13 }}>
            Category performance, city heatmaps, dealer scorecards, homeowner funnel — all will populate automatically as real orders flow in from the Dealer App and HomeOwner App.
          </div>
        </div>
      </div>
    </Spin>
  );
}